import { useState, useEffect, useMemo, useRef } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { getRunData, getRunRanking } from "../utils/api";
import { buildXlsx } from "../utils/xlsx";
import { FMT, NUMERIC } from "../utils/stockDashboardPayload";
import {
  cardSx, titleSx, capSx, countSx, vintageSx, exportButtonSx, scrollSx,
  sortRows, nextSort, headerSortProps, BRAND, ERR,
} from "./liveGridStyles";

/**
 * Model ranking by security — native grid, so it and the stock dashboard above it
 * are the same kind of thing and answer to the same filter bar.
 *
 * Replaces a generic RunDataView here for two reasons the shared component can't
 * serve: this section needs to be filterable by INDUSTRY (a column the table
 * doesn't have — see the stock->industry map) and it needs the percentile read as
 * a bar rather than a bare number.
 */

/* The verdict below this grid says "avoid the worst-performing 20% decisions", so
   the flag has to be the bottom 20% and not the bottom decile, or the grid marks
   a different set than the recommendation does. */
const AVOID = 0.2;
const TOP = 0.9;

const COLS = [
  ["security", "txt"],
  ["source", "txt"],
  ["adj_close_date", "txt"],
  ["price", "num"],
  ["quantity", "num"],
  ["net_amount", "num"],
  ["percentile", "num"],
];
const IDX = Object.fromEntries(COLS.map(([n], i) => [n, i]));

/* The backend clamps `limit` to 500 and this table is larger, so it is read in
   pages. It is only a few hundred rows in total, which is what lets the whole
   thing be held and filtered client-side — that is why one filter bar can drive
   both grids with no server round trip per selection. */
const PAGE = 500;
const MAX_ROWS = 5000;   // a guard, not an expectation: don't page forever

/* A stable ORDER BY is required, not cosmetic: the endpoint only adds one when
   `sort` is passed, and LIMIT/OFFSET paging over an unordered BigQuery result can
   repeat or skip rows between pages. The grid re-sorts client-side anyway. */
const PAGE_SORT = { sort: "security", order: "asc" };

/** Rows may arrive as objects or arrays; pin them to our column order either way. */
function normalise(data) {
  const cols = data?.columns ?? [];
  return (data?.rows ?? []).map((r) => COLS.map(([name]) => {
    if (Array.isArray(r)) {
      const at = cols.indexOf(name);
      return at === -1 ? null : r[at];
    }
    return r?.[name] ?? null;
  }));
}

/** Inflate the cached payload — same gzip+base64 transport the dashboard uses. */
async function readCached(packed) {
  if (typeof DecompressionStream !== "function") return null;   // fall back to the live read
  const bin = Uint8Array.from(atob(packed), (c) => c.charCodeAt(0));
  const stream = new Blob([bin]).stream().pipeThrough(new DecompressionStream("gzip"));
  const payload = JSON.parse(await new Response(stream).text());
  // Trust our own column order over the payload's, so a future column addition
  // server-side cannot shift what this grid renders.
  const cols = payload.cols ?? [];
  const at = COLS.map(([name]) => cols.findIndex((c) => c[0] === name));
  return (payload.rows ?? []).map((r) => at.map((i) => (i === -1 ? null : r[i])));
}

/**
 * Reads every page, handing rows up as they arrive.
 *
 * Each request costs the backend TWO BigQuery jobs (a COUNT(*) and the SELECT), so
 * the old serial do/while spent four round trips of latency before showing
 * anything. Page 1 now renders immediately and the remainder is fetched in
 * parallel, which is why this grid no longer lags the dashboard beside it — the
 * dashboard needs no query at all, its rows travel with the session document.
 */
async function fetchPages(runId, table, onRows) {
  const base = { table, limit: PAGE, ...PAGE_SORT };
  const first = await getRunData(runId, { ...base, offset: 0 });
  const total = typeof first?.total === "number" ? first.total : (first?.rows?.length ?? 0);
  onRows(normalise(first), total);

  const offsets = [];
  for (let o = PAGE; o < Math.min(total, MAX_ROWS); o += PAGE) offsets.push(o);
  if (!offsets.length) return;
  const rest = await Promise.all(offsets.map((o) => getRunData(runId, { ...base, offset: o })));
  onRows(rest.flatMap(normalise), total);
}

function PercentileCell({ value }) {
  if (value == null) return null;
  const avoid = value < AVOID;
  const top = value >= TOP;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, justifyContent: "flex-end" }}>
      <span>{(value * 100).toFixed(1)}</span>
      <Box
        component="span"
        sx={{
          height: 7, borderRadius: "3px", flex: "0 0 auto",
          width: `${Math.max(2, value * 54)}px`,
          background: avoid ? ERR : top ? `linear-gradient(90deg,${BRAND},#5ec9b8)` : "#CBD2D9",
        }}
      />
      {avoid && (
        <Box component="span" sx={{
          px: 0.75, borderRadius: 999, fontSize: ".7rem", fontWeight: 600,
          bgcolor: "#FDECEA", color: "#b3261e", whiteSpace: "nowrap",
        }}>
          avoid
        </Box>
      )}
    </Box>
  );
}

export default function ModelRankingGrid({ runId, tableName, refreshKey, industry, stock, indOf }) {
  const [all, setAll] = useState(null);
  // Kept from the first response so the denominator is the table's real size while
  // later pages are still arriving — otherwise the count would read "500 of 500"
  // and then jump.
  const [total, setTotal] = useState(0);
  const [error, setError] = useState("");
  const [sort, setSort] = useState({ idx: IDX.percentile, dir: "desc" });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!runId || !tableName) return;
    let live = true;
    setAll(null);
    setTotal(0);
    setError("");
    /* Cached payload first: one Firestore field read instead of two BigQuery jobs
       per page view. Falls back to the live paged read if the endpoint is not
       deployed yet or the browser cannot inflate, so this is safe to ship ahead of
       the backend. */
    const viaCache = async () => {
      const res = await getRunRanking(runId);
      const rows = res?.packed ? await readCached(res.packed) : null;
      if (!rows) throw new Error("no cached payload");
      if (!live) return;
      setTotal(res.rows ?? rows.length);
      setAll(rows);
    };

    viaCache()
      .catch(() => fetchPages(runId, tableName, (rows, t) => {
        if (!live) return;
        setTotal(t);
        setAll((prev) => (prev ? [...prev, ...rows] : rows));
      }))
      .catch((e) => {
        if (live) {
          setError(
            e?.response?.status === 403
              ? "You don't have access to this run's data."
              : e?.response?.data?.error || e?.message || "Could not load the ranking."
          );
        }
      });
    return () => { live = false; };
  }, [runId, tableName, refreshKey]);

  const rows = useMemo(() => {
    if (!all) return [];
    const kept = all.filter((r) => {
      const sec = r[IDX.security];
      return (!industry || (indOf && indOf.get(sec) === industry)) && (!stock || sec === stock);
    });
    return sortRows(kept, (i) => NUMERIC.has(COLS[i][1]), sort);
  }, [all, industry, stock, indOf, sort]);

  /* Selecting an industry hides every security the research universe has no row
     for — about a third of this table, nearly all funds and ETFs. Say so, rather
     than letting them quietly disappear. */
  const unclassified = useMemo(() => {
    if (!all || !indOf) return 0;
    return new Set(all.filter((r) => !indOf.get(r[IDX.security])).map((r) => r[IDX.security])).size;
  }, [all, indOf]);

  const vintage = useMemo(() => {
    if (!all || !all.length) return null;
    const dates = all.map((r) => r[IDX.adj_close_date]).filter(Boolean).sort();
    const last = dates[dates.length - 1];
    if (!last) return null;
    const d = new Date(`${String(last).slice(0, 10)}T00:00:00`);
    return Number.isNaN(d.getTime())
      ? String(last)
      : d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }, [all]);

  const exportXlsx = () => {
    const bytes = buildXlsx(COLS, rows, "Ranking");
    const url = URL.createObjectURL(new Blob([bytes], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "model_ranking.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  };

  return (
    <Box sx={cardSx}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flexWrap: "wrap" }}>
        <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>
          <Typography sx={titleSx}>Model ranking by security</Typography>
          <Typography sx={capSx}>
            Each security scored as a standard-size buy and ranked against past
            trades — higher percentile ranked better. Prioritisation, not a price
            forecast; see the recommendation below. Dated by adj_close_date.
          </Typography>
        </Box>
        <Button variant="contained" size="small" onClick={exportXlsx}
          disabled={!rows.length} sx={exportButtonSx}>
          Export to Excel
        </Button>
      </Box>

      {error && <Typography variant="body2" color="error" sx={{ pt: 2 }}>{error}</Typography>}

      {!error && !all && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress size={22} />
        </Box>
      )}

      {!error && all && (
        <>
          {vintage && <Typography sx={vintageSx}>Data as of {vintage}</Typography>}

          <Box ref={scrollRef} sx={scrollSx(420)}>
            <table>
              <thead>
                <tr>
                  {COLS.map(([label, fmt], i) => {
                    const { arrow, ...aria } = headerSortProps(sort, i);
                    return (
                      <th
                        key={label}
                        className={NUMERIC.has(fmt) ? "n" : undefined}
                        onClick={() => setSort((cur) => nextSort(cur, i))}
                        {...aria}
                      >
                        {label}
                        <span className="arw">{arrow}</span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {!rows.length && (
                  <tr>
                    <td className="empty" colSpan={COLS.length}>
                      No rows match the current selection.
                    </td>
                  </tr>
                )}
                {rows.map((r, ri) => {
                  const p = r[IDX.percentile];
                  return (
                    <tr key={ri} className={p != null && p < AVOID ? "avoid" : undefined}>
                      {COLS.map(([, fmt], i) => (
                        <td key={i} className={NUMERIC.has(fmt) ? "n" : undefined}>
                          {i === IDX.percentile
                            ? <PercentileCell value={r[i]} />
                            : FMT[fmt](r[i])}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>

          <Typography sx={countSx}>
            {rows.length.toLocaleString("en-US")} of {(total || all.length).toLocaleString("en-US")} rows
            {industry && unclassified > 0 &&
              ` · ${unclassified.toLocaleString("en-US")} funds and ETFs are not classified by industry and appear only when Industry is All`}
          </Typography>
        </>
      )}
    </Box>
  );
}
